import React, { useState, useEffect } from 'react';
import { Switch, styled } from '@mui/material';
import './style.css';
import { useUserInfo } from '../../../../context/user';
import { useAppServices } from '../../../../hook/services';
import { toast } from 'react-hot-toast';

const CustomSwitch = styled(Switch)(({ theme }) => ({
    width: 44,
    height: 24,
    padding: 0,
    '& .MuiSwitch-switchBase': {
        padding: 2,
        margin: 0,
        transitionDuration: '300ms',
        '&.Mui-checked': {
            transform: 'translateX(20px)',
            color: '#fff',
            '& + .MuiSwitch-track': {
                backgroundColor: '#0f172a',
                opacity: 1,
                border: 0,
            },
            '&.Mui-disabled + .MuiSwitch-track': {
                opacity: 0.5,
            },
        },
    },
    '& .MuiSwitch-thumb': {
        boxSizing: 'border-box',
        width: 20,
        height: 20,
    },
    '& .MuiSwitch-track': {
        borderRadius: 26 / 2,
        backgroundColor: '#E9E9EA',
        opacity: 1,
        transition: theme.transitions.create(['background-color'], {
            duration: 500,
        }),
    },
}));

const Guardrails: React.FC = () => {
    const [piiEnabled, setPiiEnabled] = useState(false);
    const [loading, setLoading] = useState(true);
    const userInfo = useUserInfo();
    const userActiveLocation = userInfo[4];
    const AppService = useAppServices();

    useEffect(() => {
        const fetchGuardrails = async () => {
            if (!userActiveLocation?.id) return;

            try {
                const { response, error } = await AppService.users.getGuardrails({
                    profileId: userActiveLocation.id
                });

                if (response?.success) {
                    setPiiEnabled(response.data?.pii_protection_enabled || false);
                } else if (error) {
                    console.error('Error fetching guardrails:', error);
                }
            } catch (err) {
                console.error('Failed to fetch guardrails:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchGuardrails();
    }, [userActiveLocation?.id]);

    const handleToggle = async () => {
        if (!userActiveLocation?.id) {
            toast.error('Profile not found');
            return;
        }

        const newValue = !piiEnabled;
        setPiiEnabled(newValue);

        try {
            const { response, error } = await AppService.users.updateGuardrails({
                profileId: userActiveLocation.id,
                payload: { piiEnabled: newValue }
            });

            if (response?.success) {
                toast.success(response.message || `PII protection ${newValue ? 'enabled' : 'disabled'}`);
            } else {
                // Revert state if failed
                setPiiEnabled(!newValue);
                toast.error(error?.message || 'Failed to update guardrails');
            }
        } catch (error) {
            // Revert state if failed
            setPiiEnabled(!newValue);
            toast.error('An unexpected error occurred');
            console.error(error);
        }
    };

    if (loading) {
        return <div className="guardrails-container">Loading...</div>;
    }

    return (
        <div className="guardrails-container">
            <div className="mb-10">
                <h1 className="guardrails-title">Guardrails</h1>
                <p className="guardrails-description">
                    Detects and redacts personally identifiable information (PII)
                </p>
            </div>

            <div className="space-y-4">
                {/* Privacy Protection Card */}
                <div className="guardrails-card">
                    <div>
                        <h2 className="card-title">Privacy Protection</h2>
                        <p className="card-subtitle">
                            Configure automatic detection and redaction of sensitive information
                        </p>
                    </div>

                    <div className="setting-row">
                        <div className="setting-info">
                            <h3 className="setting-label">
                                Personally Identifiable Information
                            </h3>
                            <p className="setting-detail">
                                Automatically detect and redact PII such as names, email addresses, phone numbers, and social security numbers
                            </p>
                        </div>
                        <div className="flex-shrink-0">
                            <CustomSwitch
                                checked={piiEnabled}
                                onChange={() => handleToggle()}
                            />
                        </div>
                    </div>
                </div>

                {/* Jailbreak Detection Card */}
                <div className="guardrails-card">
                    <div>
                        <h2 className="card-title">Jailbreak Detection</h2>
                        <p className="card-subtitle">
                            Prevent attempts to bypass agent instructions and security controls
                        </p>
                    </div>

                    <div className="">
                        <h3 className="setting-label">
                            Jailbreak
                        </h3>
                        <p className="setting-detail">
                            All agents detect jailbreaking attempts to ensure the agent stays on task
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Guardrails;